<script setup lang="ts">
  import FlowPageNavigation from '@/components/form-wizard/FlowPageNavigation.vue'
  import FlowPageSelector from '@/components/form-wizard/FlowPageSelector.vue'
  import MimicBanner from '@/components/form-wizard/MimicBanner.vue'
  import Logo from '@/components/Logo.vue'
  import { useFlowConfigStore } from '@/store/flow-config.store.ts'
  import { useFlowStore } from '@/store/flow.store.ts'
  import { FlowRenderer, UiButton, type Nullish } from '@logistiek-zonder-papier/lzp-library'
  import { v4 } from 'uuid'
  import { computed, ref, watch } from 'vue'

  const selectedFieldId = defineModel<string | undefined>('selectedFieldId')
  const selectedPageId = defineModel<string | undefined>('selectedPageId')

  const currentPage = defineModel<number>('currentPage', { required: true })
  const selectedEl = ref<HTMLElement | null>(null)

  const flowStore = useFlowStore()
  const configStore = useFlowConfigStore()
  const formFields = computed(() => configStore['config']?.pages[currentPage.value]?.fields ?? [])
  const currentFlowPage = computed(() => configStore['config']?.pages[currentPage.value])

  function handleClick(event: PointerEvent) {
    if (!(event.target instanceof HTMLElement)) return

    let target: HTMLElement | null = event.target.closest('[data-component-id]')

    while (target) {
      const parent: HTMLElement | Nullish = target.parentElement?.closest('[data-component-id]')
      if (!parent) break
      target = parent
    }

    if (selectedEl.value) {
      selectedEl.value.classList.remove('selected-component')
      selectedEl.value = null
    }

    if (!target) {
      selectedFieldId.value = undefined
      selectedPageId.value = undefined
      return
    }

    if (currentFlowPage.value && target.dataset['componentId'] === currentFlowPage.value.id) {
      selectPageId(target.dataset['componentId'])
      selectedEl.value = target
      selectedFieldId.value = undefined
    }

    target.classList.add('selected-component')
    selectedEl.value = target
    selectedFieldId.value = target.dataset['componentId']
  }

  watch(
    () => selectedFieldId.value,
    async newId => {
      if (selectedEl.value) {
        selectedEl.value.classList.remove('selected-component')
        selectedEl.value = null
      }

      if (!newId) return

      const target = document.querySelector<HTMLElement>(`[data-component-id="${newId}"]`)
      if (!target) return

      target.classList.add('selected-component')
      selectedEl.value = target
    }
  )

  function selectPageId(id: string) {
    if (selectedEl.value) {
      selectedEl.value!.classList.remove('selected-component')
      selectedEl.value = null
      selectedFieldId.value = undefined
    }

    selectedPageId.value = id
  }

  function resetMimicDeviceId() {
    configStore.resetMimicDeviceId()
    flowStore.setValue('deviceId', v4())
  }

  function resetMimicUserId() {
    configStore.resetMimicUserId()
    flowStore.setValue('userId', 'user-id')
  }

  function showSubmitText() {
    const pages = configStore.config?.pages.length
    const currentPage = configStore.currentPage

    if (!pages) return 'Verzenden'

    return pages - 1 <= currentPage ? 'Verzenden' : 'Volgende'
  }

  function navigateNextPage() {
    const pages = configStore.config?.pages.length
    if (!pages) return

    if (configStore.currentPage >= pages - 1) return

    configStore.currentPage++
  }
</script>

<template>
  <section class="layout">
    <header class="toolbar">
      <div class="mimic-banner-container">
        <MimicBanner
          v-if="configStore.mimicDeviceId"
          :id="configStore.mimicDeviceId"
          text="Mimicking device id"
          button-label="Reset Device"
          :on-reset="resetMimicDeviceId"
        />

        <MimicBanner
          v-if="configStore.mimicUserId"
          :id="configStore.mimicUserId"
          text="Mimicking app user id"
          button-label="Reset App User"
          :on-reset="resetMimicUserId"
        />
      </div>
    </header>

    <main class="content">
      <div class="page-selector-container">
        <FlowPageSelector />
      </div>

      <div class="preview-container">
        <div
          class="banner"
          :class="{ 'mimic-active': configStore.mimicUserId || configStore.mimicDeviceId }"
        >
          <Logo class="logo" />
        </div>
        <div class="preview">
          <div
            class="preview-form"
            v-if="configStore['config']"
            @click="handleClick"
            :data-selected-id="selectedFieldId"
          >
            <template v-if="formFields.length">
              <FlowRenderer
                v-model:context="flowStore.flowData"
                :dev="true"
                :config="configStore['config']"
                :page="currentPage"
                :errors="{}"
                :is-old="false"
              />
            </template>
            <div v-else class="page-empty">Select an element to preview it here</div>
            <div class="spacing"></div>

            <UiButton
              v-if="configStore.config.pages[currentPage]?.showSubmit"
              :label="showSubmitText()"
              variant="prominent"
              class="submit-button"
              @click="navigateNextPage"
            />
          </div>
        </div>
      </div>
    </main>
    <footer class="footer">
      <FlowPageNavigation />
    </footer>
  </section>
</template>

<style scoped>
  .spacing {
    padding: var(--spacing-xs);
  }

  .footer {
    width: 20rem;
    margin: 0 auto;
  }

  .page-selector-container {
    padding: var(--spacing-s);
  }

  .page-name {
    font-size: 1.25rem;
    font-weight: bold;
    color: var(--colours-grey-5);

    box-sizing: border-box;
    border-radius: 0.5rem;
    border: 0.5rem solid transparent;
  }

  .active-page-name {
    box-shadow: 0 0 0 2px var(--colours-brand-colour-1);
  }

  .page-empty {
    padding: 0.5rem;
    margin-bottom: -(var(--spacing-s));
  }

  .layout {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    height: 100%;
    width: 60%;
    background-color: var(--colours-grey-3);
    border-right:
      0.0625rem solid var(--colours-grey-2),
      0.6;
    border-left: 0.0625rem solid var(--colours-grey-2);
  }

  .toolbar {
    display: flex;
    flex-direction: column;
  }

  .tools {
    margin: var(--spacing-s);
    width: fit-content;
    background: var(--colours-grey-1);
    border: 0.0625rem solid rgb(var(--color-border));
    border-radius: 0.5rem;
    padding: 0.35rem 0.45rem;
  }

  .toolbar-icon {
    width: var(--spacing-s);
    height: var(--spacing-s);
    padding: 0.125rem;
    color: rgb(var(--toolbar-icon));
    cursor: pointer;
  }

  .toolbar-icon:hover {
    color: rgb(var(--color-black));
  }

  .toolbar-icon.active {
    color: var(--colours-brand-colour-1);
  }

  .content {
    display: flex;
    flex-direction: column;
    place-items: center;
    padding: 0 var(--spacing-s) var(--spacing-s);
  }

  .preview-container {
    width: 20rem;
    height: 35rem;
    background-color: var(--colours-grey-1);
    border-radius: var(--spacing-s);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .banner {
    background: var(--colours-brand-colour-1);
    border-radius: var(--spacing-s) var(--spacing-s) 0 0;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: var(--spacing-s);
  }

  .mimic-active {
    background: var(--colours-support-warning);
  }

  .logo {
    width: 7.5rem;
    height: 3.125rem;
  }

  .preview {
    flex: 1;
    width: 100%;
    overflow-y: hidden;
  }

  .preview-form {
    scrollbar-width: none;
    width: 100%;
    min-width: 100%;
    height: 90%;
    min-height: 90%;
    overflow: auto;
    display: flex;
    flex-direction: column;
    padding: var(--spacing-s);
  }

  form {
    width: 100%;
    height: 100%;
  }

  .submit-button {
    margin-top: auto;
    user-select: none;
    width: 100%;
    padding: 0.5rem 0.75rem;
    border-radius: 0.75rem;
    border: none;
    background: var(--colours-brand-colour-1);
    color: var(--colours-grey-1);
    font-size: 0.875rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    cursor: pointer;
    transition:
      transform 0.1s ease,
      filter 0.1s ease;
  }

  .submit-button:active {
    transform: translateY(0.0625rem);
  }

  .submit-button:hover {
    filter: brightness(1.03);
  }

  .empty {
    display: grid;
    place-items: center;
    height: 100%;
    color: var(--colours-grey-3);
    font-size: 0.9rem;
  }

  .preview-form :deep([data-component-id]) {
    box-sizing: border-box;
    border-radius: 0.5rem;
    border: 0.5rem solid transparent;
  }

  .preview-form :deep(.selected-component) {
    box-shadow: 0 0 0 2px var(--colours-brand-colour-1);
  }

  .mimic-banner-container {
    display: flex;
    flex-direction: row;
  }
</style>
